/**
 * Filter drawer for the Payments section of BillingView.
 * Uses the shared FilterDrawer for the outer drawer shell.
 *
 * Filter fields (per Figma design):
 * - Payment Date (date range)
 * - Organizations (SUPER_ADMIN only)
 * - Transaction Types (multiselect)
 */
import { Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { useState } from "react";
import { FilterDrawer, useFilterDrawerContext } from "../../../components/filters/FilterDrawer";
import {
  getDateRangeFilter,
  getInStatusFilter,
  getInitialDateRange,
  getInitialFilterValues,
  SearchInput,
  toSearchInputArray,
} from "../../../configuration/filters/searchInput";
import { MultiselectInput, MultiselectValue } from "../../../components/Multiselect";
import { transactionTypeRender } from "../../../utils/searchField.util";
import { DatePickerInput } from "../../../components/DatePicker";
import {
  getCustomerPublicIdFilter,
  getInitialOrganization,
  NO_FILTER_VALUE,
  OrganizationFilter,
} from "../../../components/filters/OrganizationFilter";
import { useFilterStyles } from "../../../components/filters/filterStyles";
import { useAuthUser } from "../../../hooks/useAuthUser";

export const path = "filter";
const filterFormId = "payments-filter-form";

type PaymentsFilterProps = {
  isOpened: boolean;
  filters: SearchInput<GraphQL.PaymentSearchFields>[];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.PaymentSearchFields>[]) => void;
};

export function PaymentsFilter({ isOpened, filters, onClose, onApply }: PaymentsFilterProps) {
  return (
    <FilterDrawer formId={filterFormId} isOpened={isOpened} filters={filters} onClose={onClose} onApply={onApply} />
  );
}

export function PaymentsFilterInner() {
  const { classes } = useFilterStyles();
  const user = useAuthUser();

  const { initialFilters, onClose, onApply } = useFilterDrawerContext<GraphQL.PaymentSearchFields>();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer: getInitialOrganization(GraphQL.PaymentSearchFields.CustomerPublicId, initialFilters),
    },
  });

  const [paymentDate, setPaymentDate] = useState<[Date | null, Date | null]>(() =>
    getInitialDateRange(GraphQL.PaymentSearchFields.CreatedAt, initialFilters)
  );
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<GraphQL.TransactionType[]>(() =>
    getInitialFilterValues<GraphQL.PaymentSearchFields, GraphQL.TransactionType>(
      GraphQL.PaymentSearchFields.TransactionType,
      initialFilters
    )
  );

  const handleApply = (customerPublicId: string) => {
    const filters = toSearchInputArray([
      getDateRangeFilter(GraphQL.PaymentSearchFields.CreatedAt, paymentDate),
      getCustomerPublicIdFilter(GraphQL.PaymentSearchFields.CustomerPublicId, customerPublicId),
      getInStatusFilter(GraphQL.PaymentSearchFields.TransactionType, transactionTypeFilter),
    ]);

    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
    setPaymentDate([null, null]);
    setTransactionTypeFilter([]);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        <DatePickerInput
          label="Payment Date"
          range
          initialMonth={paymentDate?.[0] || undefined}
          value={paymentDate}
          onChange={setPaymentDate}
        />
        {user.is.SUPER_ADMIN && <OrganizationFilter {...getInputProps("selectedCustomer")} />}
        <MultiselectInput
          label="Transaction Types"
          items={allTransactionTypes}
          value={transactionTypeFilter.map((value) => ({ id: value, label: transactionTypeRender(value) }))}
          onValueChange={(newValue) => {
            setTransactionTypeFilter(newValue.map((value) => value.id));
          }}
          onDelete={(valueToDelete) => {
            setTransactionTypeFilter((current) => current.filter((type) => type !== valueToDelete.id));
          }}
        />
      </form>
    </Stack>
  );
}

const allTransactionTypes: MultiselectValue<GraphQL.TransactionType>[] = Object.values(GraphQL.TransactionType).map(
  (type) => ({ id: type, label: transactionTypeRender(type) })
);
